import React, { useRef } from 'react'
import Dropdown from '../../Common/DropDown'
import { useState } from 'react'
import { Divider } from 'antd'
import { useNavigate } from 'react-router-dom'
import DeleteMenuPopUp from '../ScriptInfo/DeleteMenu'
import { handleFileInput, parseToBody, saveContent, updateStatus } from './utils/scriptUtils'
import { toast } from 'react-toastify'
import { formatImportedScript } from './utils/formatUtils'
import { useDispatch, useSelector } from 'react-redux'
import SaveAsModal from '../modals/SaveAsModal'
import html2pdf from 'html2pdf.js'
import { PDFDocument } from 'pdf-lib'
import { PADDING, PAGE_HEIGHT } from './utils/pageUtils'
import { exportContentStyle } from './utils/exportUtils'
import CharactersModal from './CharactersModal'
import { setIsTranslateOpen } from '../../../store/navSlice'

const DesktopMenuBar = ({ setShowScriptLoading, scriptData, editor, documentType }) => {
  const [isDeleteMenuOpen, setIsDeleteMenuOpen] = useState(false)
  const scenes = useSelector(state => state.script.scenes.allScenes)
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false)
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const pdfInputRef = useRef(null)
  const fdxInputRef = useRef(null)
  const [isLoading, setisLoading] = useState(null)
  const user = JSON.parse(localStorage.getItem('user'))

  const { title, author, copyright, byline, type, basedOn, email, phoneNumber } = scriptData || {}

  const handleDeleteClcik = () => {
    setIsDeleteMenuOpen(true)
  }

  const exportToPdf = async () => {
    if (scriptData.userId !== user._id) {
      toast.error("Only Admin is allowed to download the script")
      return
    }

    if (editor) {
      setShowScriptLoading(true)
      
      try {
        await generateClientSidePdf()
      } catch (error) {
        console.error('PDF generation error:', error)
        toast.error('Failed to generate PDF. Please try again.')
      } finally {
        setShowScriptLoading(false)
      }
    }
  }

  const generateClientSidePdf = async () => {
    const body = editor.getBody()
    const allNodes = Array.from(body.childNodes)
    
    // Calculate estimated content size
    const estimatedHeight = allNodes.length * 50 // Rough estimate: 50px per node
    const maxSafeHeight = 15000 // Safe canvas height limit for most browsers

    toast.info(`Generating PDF...`)
    
    if (estimatedHeight <= maxSafeHeight) {
      // Small document - use original method
      await generateSinglePdf(body)
    } else {
      // Large document - use chunking method
      await generateChunkedPdf(allNodes)
    }
  }

  const generateSinglePdf = async (body) => {
    const wrapper = document.createElement('pdfupload')
    wrapper.className = 'pdf-container'
    wrapper.style.display = 'block'
    wrapper.style.paddingTop = `${PADDING}px`
    wrapper.style.paddingBottom = `${20}px`
    wrapper.style.width = `${794.993}px`

    if (type !== 'TREATMENT') {
      const titleDiv = document.createElement('div')
      titleDiv.innerHTML = `
      <div style="position: relative; padding-top: 150px; padding-left: 100px; padding-right: 100px; height:${PAGE_HEIGHT}px;">
        <h1 style="text-align: center;">${title || ''}</h1>
        <h2 style="text-align: center; margin-top: 16px;">Written By ${author || ''}</h2>
        <h2 style="text-align: center; margin-top: 16px;">Based On, ${basedOn || ''}</h2>
        <h2 style="text-align: center; margin-top: 16px;">Copyright</h2>
        <h2 style="text-align: center;">${copyright || ''}</h2>
        <div style="position: absolute; bottom: 0px; left: 0px;">
          <p style=" ">E-mail: ${email || ''}</p>
          <p style=" ">Phone number: ${phoneNumber || ''}</p>
        </div>
      </div>`
      wrapper.appendChild(titleDiv)
      
      const pageBreak = parseToBody(`<div class="custom-pagebreak" data-no-continue data-page="1." />`).firstChild
      wrapper.appendChild(pageBreak)
    }

    body.childNodes.forEach(node => {
      const nodeClass = node.classList ? node.classList[0] : ''
      const cloneNode = node.cloneNode(true)
      if (nodeClass === "custom-pagebreak") {
        cloneNode.style.height = `5px`
      }
      wrapper.appendChild(cloneNode)
    })

    const styles = document.createElement('style')
    styles.innerHTML = exportContentStyle
    wrapper.prepend(styles)

    const timestamp = Math.floor(Date.now() / 1000)
    const fileName = `${scriptData.title || 'Script'} - ${timestamp}.pdf`

    await html2pdf()
      .set({
        filename: fileName,
        format: 'letter',
        autoPaging: true,
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: '.page-break-avoid'
        },
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 1.5, 
          useCORS: true,
          allowTaint: true
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      })
      .from(wrapper)
      .save()

    wrapper.remove()
    
    toast.success(`Successfully generated PDF`)
  }

  const generateChunkedPdf = async (allNodes) => {
    // Smart chunking that respects page boundaries and content relationships
    const chunks = createIntelligentChunks(allNodes)
    
    console.log(`Generating ${chunks.length} PDF parts for large document`)
    // toast.info(`Large document detected. Generating ${chunks.length} parts and merging...`)
    
    // Array to store PDF data for merging
    const pdfChunks = []
    
    // Generate each chunk as a PDF
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex]
      
      const wrapper = document.createElement('pdfupload')
      wrapper.className = 'pdf-container'
      wrapper.style.display = 'block'
      wrapper.style.paddingTop = `${PADDING}px`
      wrapper.style.paddingBottom = `${20}px`
      wrapper.style.width = `${794.993}px`
      
      // Add title page only to first chunk
      if (chunkIndex === 0 && type !== 'TREATMENT') {
        const titleDiv = document.createElement('div')
        titleDiv.innerHTML = `
        <div style="position: relative; padding-top: 150px; padding-left: 100px; padding-right: 100px; height:${PAGE_HEIGHT}px;">
          <h1 style="text-align: center;">${title || ''}</h1>
          <h2 style="text-align: center; margin-top: 16px;">Written By ${author || ''}</h2>
          <h2 style="text-align: center; margin-top: 16px;">Based On, ${basedOn || ''}</h2>
          <h2 style="text-align: center; margin-top: 16px;">Copyright</h2>
          <h2 style="text-align: center;">${copyright || ''}</h2>
          <div style="position: absolute; bottom: 0px; left: 0px;">
            <p style=" ">E-mail: ${email || ''}</p>
            <p style=" ">Phone number: ${phoneNumber || ''}</p>
          </div>
        </div>`
        wrapper.appendChild(titleDiv)
        
        const pageBreak = parseToBody(`<div class="custom-pagebreak" data-no-continue data-page="1." />`).firstChild
        wrapper.appendChild(pageBreak)
      }
      
      // Add chunk content with improved page break handling
      chunk.forEach(node => {
        const nodeClass = node.classList ? node.classList[0] : ''
        const cloneNode = node.cloneNode(true)
        
        // Preserve original page break styling for proper spacing
        if (nodeClass === "custom-pagebreak") {
          // Keep original margins for proper page flow in PDF
          const originalStyle = node.getAttribute('style') || ''
          cloneNode.setAttribute('style', originalStyle + '; page-break-before: always;')
        }
        
        wrapper.appendChild(cloneNode)
      })
      
      // Add styles
      const styles = document.createElement('style')
      styles.innerHTML = exportContentStyle
      wrapper.prepend(styles)
      
      // Use consistent settings with single PDF generation
      const chunkPdfData = await html2pdf()
        .set({
          format: 'letter',
          autoPaging: true,
          pagebreak: {
            mode: ['avoid-all', 'css', 'legacy'],
            before: '.page-break-before',
            after: '.page-break-after',
            avoid: '.page-break-avoid'
          },
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 1.5, // Use same scale as single PDF
            useCORS: true,
            allowTaint: true
          },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        })
        .from(wrapper)
        .outputPdf('arraybuffer')
      
      pdfChunks.push(chunkPdfData)
      
      // Clean up DOM
      wrapper.remove()
      
      // Show progress
      // toast.info(`Generated part ${chunkIndex + 1} of ${chunks.length}`)
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    
    // Merge all PDF chunks into a single document
    // toast.info('Merging PDF parts...')
    const mergedPdf = await PDFDocument.create()
    
    for (let i = 0; i < pdfChunks.length; i++) {
      const chunkPdf = await PDFDocument.load(pdfChunks[i])
      const pages = await mergedPdf.copyPages(chunkPdf, chunkPdf.getPageIndices())
      pages.forEach((page) => mergedPdf.addPage(page))
    }
    
    // Save the merged PDF
    const pdfBytes = await mergedPdf.save()
    const timestamp = Math.floor(Date.now() / 1000)
    const fileName = `${scriptData.title || 'Script'} - ${timestamp}.pdf`
    
    // Create download link
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast.success(`Successfully generated PDF`)
  }

  const createIntelligentChunks = (allNodes) => {
    const chunks = []
    let currentChunk = []
    let nodeCount = 0
    const maxNodesPerChunk = 120 // Reduced from 150 for better chunk boundaries
    
    for (let i = 0; i < allNodes.length; i++) {
      const node = allNodes[i]
      const nodeClass = node.classList ? node.classList[0] : ''
      
      // Check if we should start a new chunk
      const shouldCreateNewChunk = 
        nodeCount >= maxNodesPerChunk && (
          nodeClass === 'custom-pagebreak' || // Natural page boundary
          nodeClass === 'sceneHeadings' ||   // New scene
          (nodeClass === 'character' && currentChunk.length > 50) // Character block, but not too small chunks
        )
      
      if (shouldCreateNewChunk && currentChunk.length > 0) {
        chunks.push([...currentChunk])
        currentChunk = []
        nodeCount = 0
      }
      
      currentChunk.push(node)
      nodeCount++
    }
    
    // Add remaining nodes as final chunk
    if (currentChunk.length > 0) {
      chunks.push(currentChunk)
    }
    
    return chunks
  }

  const handleInput = async (e) => {
    setisLoading(true)
    setShowScriptLoading(true)
    const body = await handleFileInput(e)
    setisLoading(false)
    setShowScriptLoading(false)
    pdfInputRef.current.value = null
    fdxInputRef.current.value = null
    if (!body) {
      return
    }
    const formattedScript = formatImportedScript(body.text, documentType)

    if (editor) {
      editor.setContent(formattedScript.toString())
    }
    else {
      toast.error('editor not loaded')
    }
  }

  const handleSaveClick = () => {
    setShowScriptLoading(true)
    const onComplete = () => {
      toast.success('Saved')
      setShowScriptLoading(false)
    }
    if (editor) {
      saveContent(editor, scriptData, onComplete, scenes?.length)
    }
  }

  const handleMarkScript = async (status) => {
    setShowScriptLoading(true)
    const onComplete = () => {
      return toast.success('Saved')
    }
    await updateStatus(scriptData, status, onComplete)
    setShowScriptLoading(false)
  }

  return (
    <>
      {isSaveAsModalOpen && <SaveAsModal scripData={scriptData} isSaveAsModalOpen={isSaveAsModalOpen} setIsSaveAsModalOpen={setIsSaveAsModalOpen} />}
      {isDeleteMenuOpen && <DeleteMenuPopUp onDelete={() => { navigate(`/scripts`) }} isOpen={isDeleteMenuOpen} setIsOpen={setIsDeleteMenuOpen} id={scriptData._id} />}
      <input onChange={handleInput} ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" />
      <input onChange={handleInput} ref={fdxInputRef} type="file" accept=".fdx" className="hidden" />
      <div className={`flex items-center gap-2 ${isLoading && 'cursor-progress'}`}>
        <Dropdown
          alignment='left'
          triggerAction='hover'
          showArrow={true}
          trigger={<button className='ms-1 text-[14px] font-bold text-[#868686] hover:text-[#292929]'>File</button>}>
          <div className='w-fit min-w-[150px] flex flex-col'>

            <button onClick={() => { pdfInputRef.current.click() }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Open</button>
            <button onClick={() => { pdfInputRef.current.click() }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Import Script</button>
            <button onClick={() => { pdfInputRef.current.click() }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Upload PDF</button>
            <button onClick={() => { fdxInputRef.current.click() }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Upload FDX</button>
            <button className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Auto Format</button>
            <button onClick={() => { exportToPdf() }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Export</button>
            <button onClick={handleSaveClick} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Save</button>
            <button onClick={() => { setIsSaveAsModalOpen(true) }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Save a Copy</button>
            <button onClick={() => { handleMarkScript('INCOMPLETE') }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Mark Script in Progress</button>
            <button onClick={() => { handleMarkScript('INREVIEW') }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Mark Script In Review</button>
            <button onClick={() => { handleMarkScript('COMPLETE') }} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Mark Script Complete</button>
            <button onClick={handleDeleteClcik} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Delete</button>
          </div>
        </Dropdown>
        <Divider type='vertical' className='bg-gray-300 w-[1px] h-4' />

        <Dropdown
          alignment='left'
          triggerAction='hover'
          showArrow={true}
          trigger={<button className='ms-1 text-[14px] font-bold text-[#868686] hover:text-[#292929]'>Document</button>}>
          <div className='w-fit min-w-[150px] flex flex-col'>
            <button onClick={() => setIsCharacterModalOpen(true)} className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Characters</button>
            <button className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Headings</button>
          </div>
        </Dropdown>
        <Divider type='vertical' className='bg-gray-300 w-[1px] h-4' />
        <Dropdown
          alignment='left'
          triggerAction='hover'

          showArrow={true}
          trigger={<button className='ms-1 text-[14px] font-bold text-[#868686] hover:text-[#292929]'>Help</button>}>
          <div className='w-fit min-w-[150px] flex flex-col'>
            <button className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>Get in touch</button>
            <button className='px-6 p-2 text-[13px] text-[#868686] font-semibold text-start hover:bg-[#FFF5E6] hover:text-[#FFA300] transition-colors duration-200'>FAQ</button>
          </div>
        </Dropdown>
        <Divider type='vertical' className='bg-gray-300 w-[1px] h-4' />
        <button onClick={() => {
          dispatch(setIsTranslateOpen(true))
        }} className='ms-1 text-[14px] font-bold text-[#868686] hover:text-[#292929]'>Select Language</button>

        {/* <button onClick={()=>setAlexClose(true)} className='ms-1 text-[14px] font-bold text-[#868686] hover:text-[#292929]'>Alex AI</button> */}
        {/* <AlexAI open={isAlexOpen} onClose={()=>setAlexClose(false)} /> */}
        <CharactersModal open={isCharacterModalOpen} setOpen={setIsCharacterModalOpen} />
      </div>
    </>
  )
}

export default DesktopMenuBar